import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import SEO from "../../components/SEO";


import { useState, useEffect } from "react";
import {
  getFacility,
  getCertificates,
} from "../../services/api";
import {
  SharedHeader,
  SharedFooter,
  PageHero,
  Breadcrumb,
  NAVY,
  ORANGE,
} from "../../components/site/SharedLayout";
import useScrollToHash from "../../hooks/useScrollToHash";
import PageLoader from "../../components/site/PageLoader";

// AFTER ALL IMPORTS
// Use unpkg CDN for the worker — version-matched, works on any domain
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function FacilityPage() {
  useScrollToHash();

  // Lightbox state — clicking a cert image opens a full-screen modal
  const [lightbox, setLightbox] = useState(null);
  const [numPages, setNumPages] = useState(0);

  // Load More state
const [showAllCertificates, setShowAllCertificates] = useState(false);
const [facilityData, setFacilityData] = useState(null);
useEffect(() => {
  async function loadFacility() {
    try {
      const facility = await getFacility();
      const certificateData = await getCertificates();

      setFacilityData(facility);
      setCerts(certificateData.certificates || []);
    } catch (err) {
      // silently fail
    }
  }

  loadFacility();
}, []);
  // 7 certifications — swap `image: null` with actual certificate image imports
  // when available (e.g. import whoGmpImg from "./assets/images/certs/who-gmp.png")
const [certs, setCerts] = useState([]);
const visibleCertificates = showAllCertificates
  ? certs
  : certs.slice(0, 9);
if (!facilityData) {
  return <PageLoader />;
}
const seo = facilityData?.seo || {};
const featureIcons = [
  "🔬",
  "💊",
  "🧪",
  "🏭",
];
  return (
     <>
  <SEO
    title={seo.title}
    description={seo.description}
    canonical={seo.canonical}
    robots={seo.robots}
    keywords={seo.keywords}
    ogTitle={seo.ogTitle}
    ogDescription={seo.ogDescription}
    ogImage={seo.ogImage}
    twitterTitle={seo.twitterTitle}
    twitterDescription={seo.twitterDescription}
    twitterImage={seo.twitterImage}
    author="GLS Pharma"
  publisher="GLS Pharma"
  />
    <div style={{ fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif", margin: 0, padding: 0, color: "#222" }}>
      <SharedHeader />

      <Breadcrumb items={[{ label: "Home", path: "/" }, { label: "Facility", path: "/facility" }]} />
      <PageHero
    label={facilityData.hero.label}
    title={facilityData.hero.title}
    subtitle={facilityData.hero.description}
    bgImage={facilityData.hero.image?.url}
/>

      {/* Infrastructure Overview */}
      <section id="infrastructure" style={{ padding: "80px 80px", background: "#fff", scrollMarginTop: 80 }}>
        <div className="two-col" style={{ display: "flex", gap: 70, alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <img src={facilityData.infrastructure.image?.url}
  alt={facilityData.infrastructure.title} style={{ width: "100%", height: 360, objectFit: "cover", borderRadius: 20, boxShadow: "0 20px 50px rgba(0,0,0,0.1)" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: ORANGE, fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>{facilityData.infrastructure.small_title}</div>
            <h2 style={{ fontSize: 34, fontWeight: 800, color: NAVY, marginBottom: 20 }}>{facilityData.infrastructure.title}</h2>
            <p style={{ color: "#555", fontSize: 15, lineHeight: 1.8, marginBottom: 24 }}>
              {facilityData.infrastructure.description}
            </p>
            {facilityData.infrastructure.features?.length > 0 &&
  facilityData.infrastructure.features.map((item, i) => (
  <div
    key={i}
    style={{
      display: "flex",
      gap: 16,
      alignItems: "flex-start",
      marginBottom: 18,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        background: NAVY,
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {featureIcons[i] || "✔️"}
    </div>

    <div>
      <div
        style={{
          fontWeight: 700,
          color: NAVY,
          fontSize: 15,
          marginBottom: 4,
        }}
      >
        {item.title}
      </div>

      <div
        style={{
          color: "#667",
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {item.description}
      </div>
    </div>
  </div>
))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section id="certifications" style={{ padding: "80px 80px", background: "#f5f7fb", scrollMarginTop: 80 }}>
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <div style={{ color: ORANGE, fontWeight: 700, fontSize: 12, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>Quality Assurance</div>
          <h2 style={{ fontSize: 36, fontWeight: 800, color: NAVY, margin: 0 }}>Certifications & Approvals</h2>
          <p style={{ color: "#556", fontSize: 15, lineHeight: 1.7, maxWidth: 720, margin: "16px auto 0" }}>
            Our production house is fully equipped with machinery for liquid & dry powder injectables, capsules & tablets, and lyophilised injectables — backed by a fully functional quality control, quality assurance, and microbiology lab.
          </p>
        </div>
        <div
  className="grid-3"
  style={{
    gap: 24,
  }}
>
          {visibleCertificates.map((cert, i) => (
            <div
              key={cert.id}
              style={{
                background: "#fff",
                border: "1px solid #e5ecf9",
                borderRadius: 16,
                overflow: "hidden",
                boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
                transition: "all 0.3s ease",
                cursor: cert.pdf?.url ? "pointer" : "default",
                display: "flex",
                flexDirection: "column",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.05)";
              }}
              onClick={() => cert.pdf?.url && setLightbox(cert)}
            >
              {/* CERTIFICATE IMAGE / PLACEHOLDER */}
              <div
  style={{
    background: "#eef2f8",
    height: 320,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid #e5ecf9",
    position: "relative",
    overflow: "hidden",
  }}
>
                {cert.pdf?.url ? (
  <Document
  file={{ url: cert.pdf.url, httpHeaders: { 'Access-Control-Allow-Origin': '*' }, withCredentials: false }}
  loading="Loading..."
  onLoadSuccess={() => {}}
  onLoadError={() => {}}
>
  <Page
    pageNumber={1}
    width={180}
    renderTextLayer={false}
    renderAnnotationLayer={false}
  />
</Document>
) : (
                  <div style={{ textAlign: "center", color: "#9aa5b8", padding: 20 }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.6 }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <circle cx="10" cy="14" r="2" />
                      <path d="m10 16 2 4 2-4" />
                    </svg>
                    <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
                      Certificate Placeholder
                    </div>
                    <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>
                      Image will be added soon
                    </div>
                  </div>
                )}
                
              </div>

              {/* NAME + DESC */}
<div
  style={{
    padding: "22px 24px",
    borderLeft: `4px solid ${ORANGE}`,
    flex: 1,
  }}
>
  <div
    style={{
      fontWeight: 700,
      color: NAVY,
      fontSize: 17,
      marginBottom: 8,
    }}
  >
    {cert.title}
  </div>

  <div
    style={{
      color: "#556",
      fontSize: 13,
      lineHeight: 1.6,
    }}
  >
    {cert.description}
  </div>

</div>
            </div>
          ))}
        </div>
        {certs.length > 9 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 40,
            }}
          >
            <button
              onClick={() =>
                setShowAllCertificates(!showAllCertificates)
              }
              style={{
                background: ORANGE,
                color: "#fff",
                border: "none",
                padding: "12px 28px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              {showAllCertificates
                ? "Show Less"
                : "Load More Certificates"}
            </button>
          </div>
        )}
      </section>

      {/* LIGHTBOX — click-to-enlarge overlay (only opens if cert has an image) */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,20,37,0.88)",
            zIndex: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 40,
            cursor: "zoom-out",
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 24,
              right: 24,
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
            }}
          >
            ×
          </button>
          <div
  onClick={(e) => e.stopPropagation()}
  style={{
    width: "90vw",
    height: "90vh",
    background: "#fff",
    borderRadius: 10,
    overflow: "hidden",
  }}
>
  <div
  onContextMenu={(e) => e.preventDefault()}
  style={{
    height: "calc(100% - 60px)",
    overflowY: "auto",
    background: "#f5f5f5",
    display: "flex",
    justifyContent: "center",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
  }}
>
  <Document
    file={{ url: lightbox.pdf?.url, httpHeaders: { 'Access-Control-Allow-Origin': '*' }, withCredentials: false }}
    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
  >
    {Array.from(new Array(numPages), (_, index) => (
      <div
        key={index}
        style={{
          margin: "20px 0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Page
          pageNumber={index + 1}
          width={850}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </div>
    ))}
  </Document>
</div>

  <div
  style={{
    height: 60,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderTop: "1px solid #ddd",
    background: "#fff",
  }}
>
  <span
    style={{
      fontWeight: 700,
      color: NAVY,
      fontSize: 16,
    }}
  >
    {lightbox.title}
  </span>
</div>
</div>
        </div>
      )}

      <SharedFooter />
    </div>
    </>
  );
}

export default FacilityPage;
